import {IconButton} from 'platform/components';
import {Box, Clickable, Heading, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {reverse} from 'ramda';

import {FinancingFieldResponseBody} from '@dms/api/core';
import {FinancingCompositionLayer} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {LayerReorder} from '../types/CompositionEditorAction';
import {getLayerName} from '../utils/getLayerName';

interface LayerListProps extends TestIdProps {
  layers: FinancingCompositionLayer[];
  // Financing mode only: the data-field catalog whose labels replace `{{KEY}}` tokens in names.
  financingFields: FinancingFieldResponseBody[];
  selectedLayerId: string | null;
  onSelect: (layerId: string) => void;
  onReorder: (layerId: string, reorder: LayerReorder) => void;
  canDuplicate: boolean;
  onDuplicate: (layerId: string) => void;
  onRemove: (layerId: string) => void;
}

// A stable ref callback: React calls it only when a row becomes the selected one, so a layer
// selected from the canvas or a preview warning scrolls into view once, not on every render.
const scrollRowIntoView = (row: HTMLDivElement | null) => {
  row?.scrollIntoView({block: 'nearest'});
};

const getLayerIcon = (layer: FinancingCompositionLayer) =>
  match(layer.type)
    .with('text', () => 'content/text_format' as const)
    .with('image', () => 'image/image' as const)
    .with('rect', () => 'image/crop' as const)
    .with('photo', () => 'image/photo_camera' as const)
    .exhaustive();

/** Fills the rest of its side panel; the heading stays in view and only the rows scroll. */
export function LayerList(props: LayerListProps) {
  return (
    <VStack spacing={2} grow={1} minHeight={0}>
      <Heading size={5}>{i18n.t('entity.watermark.composition.labels.layers')}</Heading>
      <Text size="xSmall" color="secondary">
        {i18n.t('entity.watermark.composition.labels.layersHint')}
      </Text>
      <Show when={props.layers.length === 0}>
        <Text size="small" color="secondary" data-testid={suffixTestId('empty', props)}>
          {i18n.t('entity.watermark.composition.labels.noLayers')}
        </Text>
      </Show>
      <Box flex={1} minHeight={0} overflowY="auto" overflowX="hidden">
        <VStack spacing={2}>
          {reverse(props.layers).map((layer) => {
            const isSelected = layer.id === props.selectedLayerId;
            return (
              <Box
                key={layer.id}
                ref={isSelected ? scrollRowIntoView : undefined}
                backgroundColor={isSelected ? 'palettes.blue.10.100' : 'general.transparent'}
                borderRadius="small"
                padding={1}
              >
                <HStack spacing={1} align="center">
                  <Box flex={1} minWidth={0}>
                    <Clickable
                      onClick={() => props.onSelect(layer.id)}
                      data-testid={suffixTestId(`select-${layer.id}`, props)}
                    >
                      <HStack spacing={2} align="center">
                        <Icon value={getLayerIcon(layer)} size={4} />
                        <Text size="small" noWrap>
                          {getLayerName(layer, props.financingFields)}
                        </Text>
                      </HStack>
                    </Clickable>
                  </Box>
                  <IconButton
                    icon="navigation/arrow_upward"
                    size="small"
                    onClick={() => props.onReorder(layer.id, 'bringForward')}
                    data-testid={suffixTestId(`forward-${layer.id}`, props)}
                  />
                  <IconButton
                    icon="navigation/arrow_downward"
                    size="small"
                    onClick={() => props.onReorder(layer.id, 'sendBackward')}
                    data-testid={suffixTestId(`backward-${layer.id}`, props)}
                  />
                  <IconButton
                    icon="content/file_copy"
                    size="small"
                    isDisabled={!props.canDuplicate || layer.type === 'photo'}
                    onClick={() => props.onDuplicate(layer.id)}
                    data-testid={suffixTestId(`duplicate-${layer.id}`, props)}
                  />
                  <IconButton
                    icon="action/delete"
                    size="small"
                    severity="danger"
                    onClick={() => props.onRemove(layer.id)}
                    data-testid={suffixTestId(`remove-${layer.id}`, props)}
                  />
                </HStack>
              </Box>
            );
          })}
        </VStack>
      </Box>
    </VStack>
  );
}
